import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PackageCheck, Truck, PackageOpen, Users, Calendar, AlertTriangle, ChevronRight, UserRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tool-crib`;
const fmtD = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const fmtReq = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
const range = (k) => (k.start_date ? `${fmtD(k.start_date)}${k.end_date && k.end_date !== k.start_date ? ` – ${fmtD(k.end_date)}` : ''}` : 'No dates yet');

export const isCribUser = (user) => user?.role === 'tool_crib' || (user?.extra_permissions || []).includes('tool_crib');

const TILES = [
  { id: 'to_pack', title: 'Boxes to pack', icon: PackageCheck, tone: 'amber', empty: 'Nothing waiting — approved dispatches with a tool list land here.' },
  { id: 'out', title: 'Boxes out on site', icon: Truck, tone: 'blue', empty: 'No boxes out right now.' },
  { id: 'recently_unpacked', title: 'Recently unpacked', icon: PackageOpen, tone: 'emerald', empty: 'Nothing unpacked in the last 14 days.' },
];
const TONE = {
  amber: { ring: 'border-amber-300 border-t-4 border-t-amber-500', head: 'text-amber-800', badge: 'bg-amber-100 text-amber-900' },
  blue: { ring: 'border-sky-300 border-t-4 border-t-sky-600', head: 'text-sky-800', badge: 'bg-sky-100 text-sky-900' },
  emerald: { ring: 'border-emerald-300 border-t-4 border-t-emerald-600', head: 'text-emerald-800', badge: 'bg-emerald-100 text-emerald-900' },
};

const KitRow = ({ k, tile, onOpen }) => (
  <button type="button" onClick={() => onOpen(k)} data-testid={`crib-tile-kit-${k.id}`}
    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-blue-50/60 flex items-start gap-3 transition-colors">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono font-bold text-slate-900">{k.work_order_number}</span>
        <span className="text-sm text-slate-700 truncate">{k.customer_name}</span>
        <span className="text-[11px] font-mono text-slate-500">{k.kit_number}</span>
      </div>
      {k.job_description && <div className="text-xs text-slate-500 truncate">{k.job_description}</div>}
      <div className="flex items-center gap-3 flex-wrap text-xs text-slate-600 mt-1">
        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{range(k)}</span>
        {k.crew?.length > 0 && <span className="inline-flex items-center gap-1 truncate"><Users className="w-3 h-3" />{k.crew.slice(0, 4).join(', ')}{k.crew.length > 4 ? ` +${k.crew.length - 4}` : ''}</span>}
        {k.requested_by_name && <span className="inline-flex items-center gap-1 text-amber-800 font-semibold" data-testid={`crib-tile-requested-by-${k.id}`}><UserRound className="w-3 h-3" />Requested by {k.requested_by_name}{k.requested_at ? ` · ${fmtReq(k.requested_at)}` : ''}</span>}
        <span>{k.item_count} item{k.item_count !== 1 ? 's' : ''}</span>
        {tile === 'to_pack' && k.packed_count > 0 && <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-900 font-bold">packing… {k.packed_count}/{k.item_count}</span>}
        {tile === 'out' && k.job_over && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 font-bold">job ended — check in</span>}
        {tile === 'recently_unpacked' && k.missing_tools > 0 && <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-900 font-bold inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{k.missing_tools} missing</span>}
        {tile === 'recently_unpacked' && k.closed_at && <span>closed {new Date(k.closed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
      </div>
    </div>
    <ChevronRight className="w-4 h-4 text-slate-400 mt-1 shrink-0" />
  </button>
);

export default function ToolCribTiles() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    let on = true;
    const load = () => axios.get(`${API}/dashboard`).then((r) => on && setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { on = false; clearInterval(t); };
  }, []);
  if (!data) return null;
  const open = (k) => navigate(`/tool-crib?kit=${k.id}`);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="crib-tiles">
      {TILES.map((t) => {
        const rows = data[t.id] || [];
        const tone = TONE[t.tone];
        const Icon = t.icon;
        return (
          <div key={t.id} className={`rounded-xl border ${tone.ring} bg-white/95 shadow-sm p-3 flex flex-col min-h-[160px]`} data-testid={`crib-tile-${t.id}`}>
            <div className="flex items-center gap-2 px-1 mb-1">
              <Icon className={`w-5 h-5 ${tone.head}`} />
              <h3 className={`font-bold ${tone.head}`}>{t.title}</h3>
              <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${tone.badge}`} data-testid={`crib-tile-count-${t.id}`}>{rows.length}</span>
            </div>
            {rows.length === 0 ? <p className="text-sm text-slate-500 px-1 py-3">{t.empty}</p>
              : <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">{rows.slice(0, 12).map((k) => <KitRow key={k.id} k={k} tile={t.id} onOpen={open} />)}</div>}
            {rows.length > 12 && <button type="button" onClick={() => navigate('/tool-crib')} className="text-xs text-sky-700 hover:text-sky-900 font-semibold mt-2 px-1 text-left">+{rows.length - 12} more in Tool Crib →</button>}
          </div>
        );
      })}
    </div>
  );
}
